/**
 * GraphRenderer component for rendering various chart types
 * Adapted from SAT Coach project for teach-assist
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ScatterChart,
  Scatter,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceDot,
  ReferenceLine,
  Label,
} from 'recharts';
import { GraphData, GraphType, GraphAngleLabel, GraphSideLabel } from '../../Types/graph';
import NumberLine from './NumberLine';
import { RectangularPrism } from './RectangularPrism';
import { Cube } from './Cube';
import { Sphere } from './Sphere';
import { Cylinder } from './Cylinder';
import { Cone } from './Cone';
import { Pyramid } from './Pyramid';
import { TriangularPrism } from './TriangularPrism';
import { Hemisphere } from './Hemisphere';
import { Silo2D } from './Silo2D';
import {
  computePolygonCentroid,
  computeRelativeAngleLabelPosition,
  computeSideLabelPosition,
  polygonSizeScale,
} from '../../utils/geometryUtils';

interface GraphRendererProps {
  graphData: GraphData;
  className?: string;
}

type ResolvedAngleLabel = GraphAngleLabel & { x: number; y: number };
type ResolvedSideLabel = GraphSideLabel & { x: number; y: number };

// Color palette for charts - purple theme to match the app
const COLORS = ['#4f46e5', '#4338ca', '#6366f1', '#818cf8', '#10b981', '#f59e0b'];

// Custom tooltip for better display
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Box sx={{ 
        bgcolor: 'white', 
        border: '1px solid #e0e0e0', 
        borderRadius: 1, 
        p: 1.5,
        boxShadow: 2
      }}>
        {label && <Typography variant="body2" fontWeight={600} color="text.secondary" mb={0.5}>{label}</Typography>}
        {payload.map((entry: any, index: number) => (
          <Typography key={index} variant="body2" sx={{ color: entry.color }}>
            {entry.name}: <strong>{entry.value}</strong>
          </Typography>
        ))}
      </Box>
    );
  }
  return null;
};

// Custom tooltip for scatter plots to show all data fields
const ScatterTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    if (!data) return null;
    
    // Extract all fields except x and y (which are transformed)
    const displayFields = Object.entries(data)
      .filter(([key]) => key !== 'x' && key !== 'y')
      .map(([key, value]) => ({ key, value }));
    
    return (
      <Box sx={{ 
        bgcolor: 'white', 
        border: '1px solid #e0e0e0', 
        borderRadius: 1, 
        p: 1.5,
        boxShadow: 2
      }}>
        {displayFields.map((field, index) => (
          <Typography key={index} variant="body2" color="text.secondary">
            <strong style={{ textTransform: 'capitalize' }}>{field.key}:</strong>{' '}
            {String(field.value)}
          </Typography>
        ))}
        {payload[0] && (
          <>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              <strong>X:</strong> {payload[0].value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Y:</strong> {payload[0].payload.y}
            </Typography>
          </>
        )}
      </Box>
    );
  }
  return null;
};

export const GraphRenderer: React.FC<GraphRendererProps> = ({ graphData, className = '' }) => {
  const { type, data, config = {} } = graphData;
  const graphType: GraphType = type;

  // Default configuration
  const {
    title,
    xLabel = '',
    yLabel = '',
    xDomain,
    yDomain,
    showGrid = true,
    showLegend = false,
    dataKeys = [],
  } = config;

  // Common chart props
  const commonMargin = { top: 20, right: 30, left: 20, bottom: 20 };

  const requiresData = type !== 'fraction-rectangle' && type !== 'fraction-circle' && type !== 'polygon' && type !== 'circle' && type !== '3d-shape' && type !== 'coordinate_plane' && type !== 'number_line';

  // Error boundary for malformed data
  if (requiresData && (!data || data.length === 0)) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }} className={className}>
        <Typography color="text.secondary">No graph data available</Typography>
      </Box>
    );
  }

  const chartData = data ?? [];

  // Helper function to render fraction comparison (both rectangle and circle)
  const renderFractionComparison = () => {
    // Render rectangle
    const rectangleConfig = config.rectangleConfig || {
      rows: 1,
      cols: 1,
      shadedCells: [],
    };
    const {
      rows,
      cols,
      shadedCells,
      shadedColor: rectShadedColor = '#c7d2fe',
      emptyColor: rectEmptyColor = '#fff',
      outlineColor: rectOutlineColor = '#4f46e5',
      caption: rectCaption,
    } = rectangleConfig;
    const totalCells = Math.max(rows * cols, 1);
    const shadedSet = new Set(shadedCells);

    // Render circle(s)
    const circleConfig = config.fractionCircleConfig;
    const circlesArray = Array.isArray(circleConfig) ? circleConfig : [circleConfig];

    const renderCircle = (circleCfg: any, index: number) => {
      if (!circleCfg || typeof circleCfg.denominator !== 'number' || circleCfg.denominator < 1) {
        return null;
      }
      
      const {
        denominator,
        shadedSlices = [],
        shadedColor: circleShadedColor = '#c7d2fe',
        emptyColor: circleEmptyColor = '#fff',
        outlineColor: circleOutlineColor = '#4f46e5',
        caption: circleCaption,
      } = circleCfg;
      
      const n = Math.max(Math.floor(denominator), 1);
      const circleShadedSet = new Set(Array.isArray(shadedSlices) ? shadedSlices : []);
      const size = 200;
      const cx = size / 2;
      const cy = size / 2;
      const r = size / 2 - 8;

      const wedgePaths = Array.from({ length: n }).map((_, i) => {
        const start = -Math.PI / 2 + (i * 2 * Math.PI) / n;
        const end = -Math.PI / 2 + ((i + 1) * 2 * Math.PI) / n;
        const x1 = cx + r * Math.cos(start);
        const y1 = cy + r * Math.sin(start);
        const x2 = cx + r * Math.cos(end);
        const y2 = cy + r * Math.sin(end);
        const angleDiff = end - start;
        const large = Math.abs(angleDiff) > Math.PI ? 1 : 0;
        const path = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
        const isShaded = circleShadedSet.has(i);
        return { path, isShaded };
      });

      return (
        <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <Box component="svg" viewBox={`0 0 ${size} ${size}`} width={size} height={size} sx={{ display: 'block' }}>
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={circleOutlineColor}
              strokeWidth={2}
            />
            {wedgePaths.map(({ path, isShaded }, idx) => (
              <path
                key={idx}
                d={path}
                fill={isShaded ? circleShadedColor : circleEmptyColor}
                stroke={circleOutlineColor}
                strokeWidth={1.5}
              />
            ))}
          </Box>
        </Box>
      );
    };

    const renderedCircles = circlesArray
      .map((circleCfg, idx) => renderCircle(circleCfg, idx))
      .filter(circle => circle !== null);

    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: 4,
        flexWrap: 'wrap'
      }}>
        {/* Rectangle visualization */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${cols}, 40px)`,
              gridTemplateRows: `repeat(${rows}, 40px)`,
              border: `2px solid ${rectOutlineColor}`,
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            {Array.from({ length: totalCells }).map((_, index) => {
              const isShaded = shadedSet.has(index);
              return (
                <Box
                  key={index}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: isShaded ? rectShadedColor : rectEmptyColor,
                    border: `1px solid ${rectOutlineColor}`,
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Circle visualization(s) */}
        {renderedCircles.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: renderedCircles.length > 1 ? 'row' : 'column',
            alignItems: 'center', 
            gap: 3,
            flexWrap: 'wrap'
          }}>
            {renderedCircles}
          </Box>
        )}
      </Box>
    );
  };

  const renderChart = () => {
    try {
      // Special case: If both rectangleConfig and fractionCircleConfig exist, render both for comparison
      if (config.rectangleConfig && config.fractionCircleConfig && 
          (graphType === 'fraction-rectangle' || graphType === 'fraction-circle')) {
        return renderFractionComparison();
      }
      
      switch (graphType) {
        case 'line': {
          // Line chart for functions and trends
          const baseKeys = chartData[0] ? Object.keys(chartData[0]) : [];
          
          // Auto-detect x-axis key (use config.xKey if specified)
          let xAxisKey = config.xKey || 'x';
          if (!config.xKey && !baseKeys.includes('x')) {
            // Look for common x-axis keys
            if (baseKeys.includes('week')) xAxisKey = 'week';
            else if (baseKeys.includes('month')) xAxisKey = 'month';
            else if (baseKeys.includes('year')) xAxisKey = 'year';
            else if (baseKeys.includes('day')) xAxisKey = 'day';
            else if (baseKeys.includes('date')) xAxisKey = 'date';
            else if (baseKeys.includes('time')) xAxisKey = 'time';
            else if (baseKeys.includes('period')) xAxisKey = 'period';
            else if (baseKeys.includes('index')) xAxisKey = 'index';
            else {
              const yKeys = new Set(dataKeys);
              const potentialXKey = baseKeys.find(k => 
                k !== 'name' && k !== 'label' && !yKeys.has(k)
              );
              if (potentialXKey) xAxisKey = potentialXKey;
            }
          }
          
          const keys = dataKeys.length > 0 ? dataKeys : baseKeys.filter(k => k !== xAxisKey && k !== 'name');
          
          // Determine if domain includes origin (for mathematical graphs)
          const xDomainIncludesZero = xDomain && xDomain[0] <= 0 && xDomain[1] >= 0;
          const yDomainIncludesZero = yDomain && yDomain[0] <= 0 && yDomain[1] >= 0;
          const showOriginAxes = xDomainIncludesZero && yDomainIncludesZero;
          
          return (
            <LineChart data={chartData} margin={commonMargin}>
              {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />}
              <XAxis
                dataKey={xAxisKey}
                label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -5 } : undefined}
                domain={xDomain}
                type="number"
                stroke="#666"
              />
              <YAxis
                label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft' } : undefined}
                domain={yDomain}
                stroke="#666"
              />
              {showOriginAxes && (
                <>
                  <ReferenceLine x={0} stroke="#333" strokeWidth={2} />
                  <ReferenceLine y={0} stroke="#333" strokeWidth={2} />
                </>
              )}
              <Tooltip content={<CustomTooltip />} />
              {showLegend && <Legend />}
              {keys.map((key, index) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={COLORS[index % COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              ))}
              {config.annotations?.map((annotation, idx) => (
                <ReferenceDot
                  key={`annotation-${idx}`}
                  x={annotation.x}
                  y={annotation.y}
                  r={6}
                  fill={annotation.color || '#ef4444'}
                  stroke="#fff"
                  strokeWidth={2}
                >
                  {annotation.label && (
                    <Label
                      value={annotation.label}
                      position="top"
                      offset={10}
                      fill={annotation.color || '#ef4444'}
                      fontSize={12}
                      fontWeight="bold"
                    />
                  )}
                </ReferenceDot>
              ))}
            </LineChart>
          );
        }

        case 'bar': {
          // Bar chart for categorical data - auto-detect x-axis key
          const firstItem = chartData[0] || {};
          const allKeys = Object.keys(firstItem);
          
          // Detect x-axis key (category/name label)
          let categoryKey = 'category';
          if (allKeys.includes('category')) categoryKey = 'category';
          else if (allKeys.includes('name')) categoryKey = 'name';
          else if (allKeys.includes('label')) categoryKey = 'label';
          else if (allKeys.length > 0) categoryKey = allKeys[0];
          
          const keys = dataKeys.length > 0 ? dataKeys : allKeys.filter(k => k !== categoryKey && typeof firstItem[k] === 'number');
          
          return (
            <BarChart data={chartData} margin={commonMargin}>
              {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />}
              <XAxis
                dataKey={categoryKey}
                label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -5 } : undefined}
                stroke="#666"
              />
              <YAxis
                label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft' } : undefined}
                domain={yDomain}
                stroke="#666"
              />
              <Tooltip content={<CustomTooltip />} />
              {showLegend && <Legend />}
              {keys.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  fill={COLORS[index % COLORS.length]}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          );
        }

        case 'histogram': {
          // Histogram for frequency distributions
          const firstItem = chartData[0] || {};
          const allKeys = Object.keys(firstItem);
          
          // Detect x-axis key (bin/range label)
          let binKey = 'bin';
          if (allKeys.includes('bin')) binKey = 'bin';
          else if (allKeys.includes('range')) binKey = 'range';
          else if (allKeys.includes('category')) binKey = 'category';
          else if (allKeys.includes('label')) binKey = 'label';
          else if (allKeys.includes('name')) binKey = 'name';
          else if (allKeys.length > 0) binKey = allKeys[0];
          
          // Detect y-axis key (frequency/count)
          let countKey = 'count';
          if (allKeys.includes('count')) countKey = 'count';
          else if (allKeys.includes('frequency')) countKey = 'frequency';
          else if (allKeys.includes('value')) countKey = 'value';
          else {
            const numericKey = allKeys.find(k => 
              k !== binKey && typeof firstItem[k] === 'number'
            );
            if (numericKey) countKey = numericKey;
          }
          
          return (
            <BarChart 
              data={chartData} 
              margin={commonMargin}
              barCategoryGap={0}
            >
              {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />}
              <XAxis
                dataKey={binKey}
                label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -5 } : undefined}
                stroke="#666"
              />
              <YAxis
                label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft' } : undefined}
                domain={yDomain}
                stroke="#666"
              />
              <Tooltip content={<CustomTooltip />} />
              {showLegend && <Legend />}
              <Bar
                dataKey={countKey}
                fill={COLORS[0]}
                stroke={COLORS[0]}
                strokeWidth={1}
              />
            </BarChart>
          );
        }

        case 'coordinate_plane': {
          // Custom SVG coordinate plane: grid, explicit axis numbers (0,1,2,...), and all points with labels
          const coordShowGrid = config.showGrid !== false;
          const coordXLabel = xLabel || 'x-axis';
          const coordYLabel = yLabel || 'y-axis';

          const raw = (data || []) as Array<Record<string, unknown>>;
          const xKey = config.xKey && raw[0] && config.xKey in raw[0] ? config.xKey : 'x';
          const yKey = config.yKey && raw[0] && config.yKey in raw[0] ? config.yKey : 'y';
          const points = raw.map((item: any) => {
            const x = typeof item[xKey] === 'number' ? item[xKey] : Number(item[xKey] ?? item.x ?? 0);
            const y = typeof item[yKey] === 'number' ? item[yKey] : Number(item[yKey] ?? item.y ?? 0);
            return {
              x: Number.isFinite(x) ? x : 0,
              y: Number.isFinite(y) ? y : 0,
              label: String(item.label ?? item.name ?? ''),
            };
          });

          const pad = 1;
          const xMin = xDomain?.[0] ?? (points.length ? Math.min(0, ...points.map(p => p.x)) - pad : 0);
          const xMax = xDomain?.[1] ?? (points.length ? Math.max(0, ...points.map(p => p.x)) + pad : 10);
          const yMin = yDomain?.[0] ?? (points.length ? Math.min(0, ...points.map(p => p.y)) - pad : 0);
          const yMax = yDomain?.[1] ?? (points.length ? Math.max(0, ...points.map(p => p.y)) + pad : 10);
          const xSpan = Math.max(1, xMax - xMin);
          const ySpan = Math.max(1, yMax - yMin);

          const w = 420;
          const h = 320;
          const ml = 44;
          const mr = 24;
          const mt = 24;
          const mb = 44;
          const plotW = w - ml - mr;
          const plotH = h - mt - mb;
          const xScale = (v: number) => ml + ((v - xMin) / xSpan) * plotW;
          const yScale = (v: number) => mt + plotH - ((v - yMin) / ySpan) * plotH;

          const xTicks: number[] = [];
          const yTicks: number[] = [];
          for (let i = Math.floor(xMin); i <= Math.ceil(xMax); i++) {
            if (i >= xMin - 0.01 && i <= xMax + 0.01) xTicks.push(i);
          }
          for (let i = Math.floor(yMin); i <= Math.ceil(yMax); i++) {
            if (i >= yMin - 0.01 && i <= yMax + 0.01) yTicks.push(i);
          }
          if (xTicks.length === 0) xTicks.push(0);
          if (yTicks.length === 0) yTicks.push(0);

          const ox = xScale(0);
          const oy = yScale(0);
          const drawXAxis = 0 >= xMin - 0.01 && 0 <= xMax + 0.01;
          const drawYAxis = 0 >= yMin - 0.01 && 0 <= yMax + 0.01;

          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
              <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', maxWidth: 480, height: 320 }} preserveAspectRatio="xMidYMid meet">
                {/* Grid */}
                {coordShowGrid && (
                  <g stroke="#e5e7eb" strokeWidth={0.6} strokeDasharray="4 2">
                    {xTicks.map((t) => (
                      <line key={`gx-${t}`} x1={xScale(t)} y1={mt} x2={xScale(t)} y2={mt + plotH} />
                    ))}
                    {yTicks.map((t) => (
                      <line key={`gy-${t}`} x1={ml} y1={yScale(t)} x2={ml + plotW} y2={yScale(t)} />
                    ))}
                  </g>
                )}
                {/* Axes */}
                {drawYAxis && (
                  <line x1={ox} y1={mt} x2={ox} y2={mt + plotH} stroke="#374151" strokeWidth={2} />
                )}
                {drawXAxis && (
                  <line x1={ml} y1={oy} x2={ml + plotW} y2={oy} stroke="#374151" strokeWidth={2} />
                )}
                {/* X tick marks and labels */}
                {xTicks.map((t) => (
                  <g key={`tx-${t}`}>
                    <line
                      x1={xScale(t)}
                      y1={drawXAxis ? oy : mt + plotH}
                      x2={xScale(t)}
                      y2={(drawXAxis ? oy : mt + plotH) + 6}
                      stroke="#374151"
                      strokeWidth={1.5}
                    />
                    <text
                      x={xScale(t)}
                      y={(drawXAxis ? oy : mt + plotH) + 20}
                      textAnchor="middle"
                      fontSize={12}
                      fill="#374151"
                      fontWeight={600}
                    >
                      {t}
                    </text>
                  </g>
                ))}
                {/* Y tick marks and labels */}
                {yTicks.map((t) => (
                  <g key={`ty-${t}`}>
                    <line
                      x1={drawYAxis ? ox : ml}
                      y1={yScale(t)}
                      x2={(drawYAxis ? ox : ml) - 6}
                      y2={yScale(t)}
                      stroke="#374151"
                      strokeWidth={1.5}
                    />
                    <text
                      x={(drawYAxis ? ox : ml) - 10}
                      y={yScale(t)}
                      textAnchor="end"
                      dominantBaseline="central"
                      fontSize={12}
                      fill="#374151"
                      fontWeight={600}
                    >
                      {t}
                    </text>
                  </g>
                ))}
                {/* Axis titles */}
                <text x={ml + plotW / 2} y={h - 8} textAnchor="middle" fontSize={11} fill="#6b7280" fontWeight={500}>
                  {coordXLabel}
                </text>
                <text
                  x={10}
                  y={mt + plotH / 2}
                  textAnchor="middle"
                  fontSize={11}
                  fill="#6b7280"
                  fontWeight={500}
                  transform={`rotate(-90, 10, ${mt + plotH / 2})`}
                >
                  {coordYLabel}
                </text>
                {/* Points */}
                {points.map((p, i) => (
                  <g key={i}>
                    <circle
                      cx={xScale(p.x)}
                      cy={yScale(p.y)}
                      r={6}
                      fill={COLORS[0]}
                      stroke="#fff"
                      strokeWidth={2}
                    />
                    {p.label && (
                      <text
                        x={xScale(p.x)}
                        y={yScale(p.y) - 12}
                        textAnchor="middle"
                        fontSize={13}
                        fill="#1f2937"
                        fontWeight={700}
                      >
                        {p.label}
                      </text>
                    )}
                    <text
                      x={xScale(p.x)}
                      y={yScale(p.y) + 18}
                      textAnchor="middle"
                      fontSize={10}
                      fill="#6b7280"
                    >
                      ({p.x}, {p.y})
                    </text>
                  </g>
                ))}
              </svg>
            </Box>
          );
        }

        case 'scatter': {
          // Scatter plot for correlation and relationships
          let xKey = config.xKey || 'x';
          let yKey = config.yKey || 'y';
          
          // Auto-detect keys if not specified
          if (!config.xKey && !config.yKey && chartData.length > 0) {
            const firstItem = chartData[0];
            if (!('x' in firstItem) || !('y' in firstItem)) {
              const keys = Object.keys(firstItem).filter(k => 
                k !== 'name' && k !== 'label' && k !== 'student' && k !== 'id'
              );
              
              if (keys.includes('hours') && keys.includes('score')) {
                xKey = 'hours';
                yKey = 'score';
              } else if (keys.includes('xValue') && keys.includes('yValue')) {
                xKey = 'xValue';
                yKey = 'yValue';
              } else if (keys.length >= 2) {
                const numericKeys = keys.filter(k => 
                  typeof firstItem[k] === 'number' || !isNaN(Number(firstItem[k]))
                );
                if (numericKeys.length >= 2) {
                  xKey = numericKeys[0];
                  yKey = numericKeys[1];
                }
              }
            }
          }
          
          // Transform data
          const transformedData = chartData.map((item) => {
            if ('x' in item && 'y' in item && typeof item.x === 'number' && typeof item.y === 'number') {
              return item;
            }
            const xValue = item[xKey];
            const yValue = item[yKey];
            
            return {
              ...item,
              x: typeof xValue === 'number' ? xValue : Number(xValue),
              y: typeof yValue === 'number' ? yValue : Number(yValue),
            };
          });

          return (
            <ScatterChart margin={commonMargin}>
              {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />}
              <XAxis
                type="number"
                dataKey="x"
                label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -5 } : undefined}
                domain={xDomain}
                stroke="#666"
              />
              <YAxis
                type="number"
                dataKey="y"
                label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft' } : undefined}
                domain={yDomain}
                stroke="#666"
              />
              <Tooltip content={<ScatterTooltip />} cursor={{ strokeDasharray: '3 3' }} />
              {showLegend && <Legend />}
              <Scatter
                name="Data Points"
                data={transformedData}
                fill={COLORS[0]}
              />
            </ScatterChart>
          );
        }

        case 'area': {
          // Area chart for cumulative data
          const areaBaseKeys = chartData[0] ? Object.keys(chartData[0]) : [];
          
          let areaXAxisKey = config.xKey || 'x';
          if (!config.xKey && !areaBaseKeys.includes('x')) {
            if (areaBaseKeys.includes('week')) areaXAxisKey = 'week';
            else if (areaBaseKeys.includes('month')) areaXAxisKey = 'month';
            else if (areaBaseKeys.includes('year')) areaXAxisKey = 'year';
            else if (areaBaseKeys.includes('day')) areaXAxisKey = 'day';
            else if (areaBaseKeys.includes('date')) areaXAxisKey = 'date';
            else if (areaBaseKeys.includes('time')) areaXAxisKey = 'time';
            else if (areaBaseKeys.includes('period')) areaXAxisKey = 'period';
            else if (areaBaseKeys.includes('index')) areaXAxisKey = 'index';
            else {
              const yKeys = new Set(dataKeys);
              const potentialXKey = areaBaseKeys.find(k => 
                k !== 'name' && k !== 'label' && !yKeys.has(k)
              );
              if (potentialXKey) areaXAxisKey = potentialXKey;
            }
          }
          
          const keys = dataKeys.length > 0 ? dataKeys : areaBaseKeys.filter(k => k !== areaXAxisKey && k !== 'name');
          
          return (
            <AreaChart data={chartData} margin={commonMargin}>
              {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />}
              <XAxis
                dataKey={areaXAxisKey}
                label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -5 } : undefined}
                domain={xDomain}
                stroke="#666"
              />
              <YAxis
                label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft' } : undefined}
                domain={yDomain}
                stroke="#666"
              />
              <Tooltip content={<CustomTooltip />} />
              {showLegend && <Legend />}
              {keys.map((key, index) => (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={COLORS[index % COLORS.length]}
                  fill={COLORS[index % COLORS.length]}
                  fillOpacity={0.6}
                />
              ))}
            </AreaChart>
          );
        }

        case 'dotplot': {
          // Dot plot: values on x-axis, stacked dots above each (one dot per observation)
          const dpConfig = config.dotplotConfig as { valueKey?: string; countKey?: string } | undefined;
          const valueKey = dpConfig?.valueKey ?? 'value';
          const countKey = dpConfig?.countKey ?? 'count';
          const rows = chartData.map((row: Record<string, unknown>) => ({
            value: Number(row[valueKey]) ?? 0,
            count: Math.max(0, Number(row[countKey]) ?? 1),
          }));
          const width = 400;
          const height = 180;
          const margin = { top: 16, right: 24, left: 24, bottom: 36 };
          const plotWidth = width - margin.left - margin.right;
          const dotRadius = 5;
          const dotGap = 6;
          const rowHeight = dotRadius * 2 + dotGap;
          const n = rows.length;
          const xScale = (i: number) => margin.left + (n <= 1 ? plotWidth / 2 : (i / (n - 1)) * plotWidth);
          const xLabel = config.xLabel ?? 'Value';

          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', maxWidth: 420, height: 'auto' }}>
                {/* x-axis line */}
                <line
                  x1={margin.left}
                  y1={height - margin.bottom}
                  x2={width - margin.right}
                  y2={height - margin.bottom}
                  stroke="#374151"
                  strokeWidth={1.5}
                />
                {rows.map((r, i) => {
                  const cx = xScale(i);
                  const baseY = height - margin.bottom - 8;
                  return (
                    <g key={i}>
                      {Array.from({ length: r.count }).map((_, j) => (
                        <circle
                          key={j}
                          cx={cx}
                          cy={baseY - j * rowHeight - dotRadius}
                          r={dotRadius}
                          fill="#4f46e5"
                          stroke="#3730a3"
                          strokeWidth={1}
                        />
                      ))}
                      <text
                        x={cx}
                        y={height - 6}
                        textAnchor="middle"
                        fontSize={12}
                        fill="#374151"
                        fontWeight={500}
                      >
                        {r.value}
                      </text>
                    </g>
                  );
                })}
                {xLabel && (
                  <text
                    x={margin.left + plotWidth / 2}
                    y={height - 2}
                    textAnchor="middle"
                    fontSize={11}
                    fill="#6b7280"
                  >
                    {xLabel}
                  </text>
                )}
              </svg>
            </Box>
          );
        }

        case 'number_line': {
          const numberLineConfig = config.numberLineConfig;
          if (!numberLineConfig) {
            return (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No number line configuration provided.</Typography>
              </Box>
            );
          }
          return <NumberLine config={numberLineConfig} />;
        }

        case 'table': {
          // Table / frequency table: bordered layout with headers and rows
          const tableConfig = config.tableConfig as { headers?: string[]; columnKeys?: string[] } | undefined;
          const headers = tableConfig?.headers ?? (chartData[0] ? Object.keys(chartData[0] as Record<string, unknown>) : []);
          const columnKeys = tableConfig?.columnKeys ?? headers;
          const cellSx = { border: '1px solid #374151', p: '10px 14px', textAlign: 'left' as const };
          return (
            <Box
              component="table"
              sx={{
                width: '100%',
                maxWidth: 480,
                borderCollapse: 'collapse',
                fontSize: '0.95rem',
              }}
            >
              <Box component="thead" sx={{ bgcolor: 'grey.100' }}>
                <Box component="tr">
                  {headers.map((h, i) => (
                    <Box
                      key={i}
                      component="th"
                      sx={{
                        ...cellSx,
                        fontWeight: 600,
                        bgcolor: 'grey.200',
                      }}
                    >
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {chartData.map((row: Record<string, unknown>, ri: number) => (
                  <Box key={ri} component="tr">
                    {columnKeys.map((key, ci) => (
                      <Box key={ci} component="td" sx={cellSx}>
                        {String(row[key] ?? '')}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          );
        }

        case 'pie': {
          // Pie chart for proportions
          return (
            <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={(entry) => `${entry.name}: ${entry.value}`}
              >
                {chartData.map((_entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              {showLegend && <Legend />}
            </PieChart>
          );
        }

        case 'fraction-rectangle': {
          const rectangleConfig = config.rectangleConfig || {
            rows: 1,
            cols: 1,
            shadedCells: [],
          };
          const {
            rows,
            cols,
            shadedCells,
            shadedColor = '#c7d2fe', // Light indigo to match theme
            emptyColor = '#fff',
            outlineColor = '#4f46e5',
            caption,
          } = rectangleConfig;
          const totalCells = Math.max(rows * cols, 1);
          const shadedSet = new Set(shadedCells);

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${cols}, 40px)`,
                  gridTemplateRows: `repeat(${rows}, 40px)`,
                  border: `2px solid ${outlineColor}`,
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                {Array.from({ length: totalCells }).map((_, index) => {
                  const isShaded = shadedSet.has(index);
                  return (
                    <Box
                      key={index}
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: isShaded ? shadedColor : emptyColor,
                        border: `1px solid ${outlineColor}`,
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          );
        }

        case 'fraction-circle': {
          const circleConfig = config.fractionCircleConfig;
          
          // Handle missing or invalid config
          if (!circleConfig) {
            // Debug: log what we actually received
            console.warn('Fraction circle config missing:', {
              type,
              hasConfig: !!config,
              configKeys: config ? Object.keys(config) : [],
              fullConfig: config,
              graphData: graphData
            });
            return (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No fraction circle configuration provided.</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontSize: '0.75rem' }}>
                  The graph type is set to "fraction-circle" but the fractionCircleConfig field is missing in the graph configuration.
                </Typography>
              </Box>
            );
          }
          
          // Support both single circle config and array of circles (for multiple pizzas, etc.)
          const circlesArray = Array.isArray(circleConfig) ? circleConfig : [circleConfig];
          
          // Validate that we have at least one valid circle config
          if (circlesArray.length === 0 || !circlesArray.some(cfg => cfg && typeof cfg.denominator === 'number' && cfg.denominator > 0)) {
            return (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">Invalid fraction circle configuration.</Typography>
              </Box>
            );
          }
          
          const renderCircle = (circleCfg: any, index: number) => {
            // Validate and extract config with defaults
            if (!circleCfg || typeof circleCfg.denominator !== 'number' || circleCfg.denominator < 1) {
              return null;
            }
            
            const {
              denominator,
              shadedSlices = [],
              shadedColor = '#c7d2fe',
              emptyColor = '#fff',
              outlineColor = '#4f46e5',
              caption,
            } = circleCfg;
            
            const n = Math.max(Math.floor(denominator), 1); // Ensure integer and positive
            const shadedSet = new Set(Array.isArray(shadedSlices) ? shadedSlices : []);
            const size = 200;
            const cx = size / 2;
            const cy = size / 2;
            const r = size / 2 - 8;

            const wedgePaths = Array.from({ length: n }).map((_, i) => {
              const start = -Math.PI / 2 + (i * 2 * Math.PI) / n;
              const end = -Math.PI / 2 + ((i + 1) * 2 * Math.PI) / n;
              const x1 = cx + r * Math.cos(start);
              const y1 = cy + r * Math.sin(start);
              const x2 = cx + r * Math.cos(end);
              const y2 = cy + r * Math.sin(end);
              
              // For semicircles (n=2), use large arc flag
              // For other cases, determine if arc is > 180 degrees
              const angleDiff = end - start;
              const large = Math.abs(angleDiff) > Math.PI ? 1 : 0;
              
              const path = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
              const isShaded = shadedSet.has(i);
              return { path, isShaded };
            });

            return (
              <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Box component="svg" viewBox={`0 0 ${size} ${size}`} width={size} height={size} sx={{ display: 'block' }}>
                  {/* Draw full circle outline first */}
                  <circle
                    cx={cx}
                    cy={cy}
                    r={r}
                    fill="none"
                    stroke={outlineColor}
                    strokeWidth={2}
                  />
                  {/* Draw wedges */}
                  {wedgePaths.map(({ path, isShaded }, idx) => (
                    <path
                      key={idx}
                      d={path}
                      fill={isShaded ? shadedColor : emptyColor}
                      stroke={outlineColor}
                      strokeWidth={1.5}
                    />
                  ))}
                </Box>
              </Box>
            );
          };

          const renderedCircles = circlesArray
            .map((circleCfg, idx) => renderCircle(circleCfg, idx))
            .filter(circle => circle !== null);

          if (renderedCircles.length === 0) {
            return (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No valid circles to render.</Typography>
              </Box>
            );
          }

          return (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: renderedCircles.length > 1 ? 'row' : 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              gap: 3,
              flexWrap: 'wrap'
            }}>
              {renderedCircles}
            </Box>
          );
        }

        case 'polygon': {
          const polygonConfig = config.polygonConfig;
          if (!polygonConfig || !polygonConfig.points || polygonConfig.points.length < 3) {
            return (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">Invalid polygon data.</Typography>
              </Box>
            );
          }

          const {
            points,
            extraLines = [],
            angleLabels: rawAngleLabels = [],
            sideLabels: rawSideLabels = [],
            pointLabelOffset,
            strokeColor = '#4f46e5', // Indigo to match new theme
            fillColor = '#e0e7ff', // Light indigo
            height: svgHeight = 320,
          } = polygonConfig;

          const viewBox = '0 0 100 100';
          const pointPath = points.map((pt) => `${pt.x},${pt.y}`).join(' ');
          const resolvedHeight = Math.min(svgHeight, 400);
          const polygonScale = polygonSizeScale(points);
          const centroid = computePolygonCentroid(points);

          let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
          points.forEach(p => {
            if (p.x < minX) minX = p.x;
            if (p.x > maxX) maxX = p.x;
            if (p.y < minY) minY = p.y;
            if (p.y > maxY) maxY = p.y;
          });
          const currentCenterX = (minX + maxX) / 2;
          const currentCenterY = (minY + maxY) / 2;
          const dx = 50 - currentCenterX;
          const dy = 50 - currentCenterY;

          const resolvedAngleLabels: ResolvedAngleLabel[] = rawAngleLabels
            .map((label) => {
              if (label.atVertex) {
                const computed = computeRelativeAngleLabelPosition(label, points, polygonScale);
                if (computed?.x !== undefined && computed?.y !== undefined) {
                  return computed;
                }
              }

              if (label.x === undefined || label.y === undefined) {
                return null;
              }

              return {
                ...label,
                x: label.x + (label.offset?.x ?? 0),
                y: label.y + (label.offset?.y ?? 0),
              };
            })
            .filter((label): label is ResolvedAngleLabel => Boolean(label));

          const resolvedSideLabels: ResolvedSideLabel[] = rawSideLabels
            .map((label) => {
              if (label.onSide) {
                const computed = computeSideLabelPosition(label, points, centroid, polygonScale);
                if (computed?.x !== undefined && computed?.y !== undefined) {
                  return computed;
                }
              }

              if (label.x === undefined || label.y === undefined) {
                return null;
              }

              return {
                ...label,
                x: label.x + (label.offset?.x ?? 0),
                y: label.y + (label.offset?.y ?? 0),
              };
            })
            .filter((label): label is ResolvedSideLabel => Boolean(label));

          return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <svg
                viewBox={viewBox}
                preserveAspectRatio="xMidYMid meet"
                style={{ width: '100%', maxWidth: '420px', height: `${resolvedHeight}px`, display: 'block' }}
              >
                <g transform={`translate(${dx}, ${dy})`}>
                  <polygon
                    points={pointPath}
                    fill={fillColor}
                    stroke={strokeColor}
                    strokeWidth={2}
                    opacity={0.35}
                  />
                  <polyline
                    points={pointPath}
                    fill="none"
                    stroke={strokeColor}
                    strokeWidth={2.5}
                  />
                  <line
                    x1={points[points.length - 1].x}
                    y1={points[points.length - 1].y}
                    x2={points[0].x}
                    y2={points[0].y}
                    stroke={strokeColor}
                    strokeWidth={2.5}
                  />

                  {extraLines.map((line, index) => {
                    const fromPoint = points[line.from];
                    const toPoint = points[line.to];
                    if (!fromPoint || !toPoint) return null;
                    return (
                      <line
                        key={`extra-line-${index}`}
                        x1={fromPoint.x}
                        y1={fromPoint.y}
                        x2={toPoint.x}
                        y2={toPoint.y}
                        stroke={strokeColor}
                        strokeWidth={1.8}
                        strokeDasharray={line.dashed ? '4 4' : undefined}
                      />
                    );
                  })}

                  {points.map((point, index) => (
                    <g key={`point-${index}`}>
                      <circle cx={point.x} cy={point.y} r={2.2} fill={strokeColor} />
                      {point.label && (
                        <text
                          x={point.x + (point.labelOffset?.x ?? (pointLabelOffset?.x ?? 2))}
                          y={point.y + (point.labelOffset?.y ?? (pointLabelOffset?.y ?? -2))}
                          fontSize={4.5}
                          fill="#0f172a"
                          fontWeight={600}
                        >
                          {point.label}
                        </text>
                      )}
                    </g>
                  ))}

                  {resolvedAngleLabels.map((label, index) => (
                    <text
                      key={`angle-${index}`}
                      x={label.x}
                      y={label.y}
                      fontSize={4.5}
                      fill="#111827"
                      textAnchor={label.align ?? 'middle'}
                      fontWeight={600}
                    >
                      {label.text}
                    </text>
                  ))}

                  {resolvedSideLabels.map((label, index) => (
                    <g key={`side-${index}`}>
                      {/* Halo for readability over lines */}
                      <text
                        x={label.x}
                        y={label.y}
                        fontSize={3.8}
                        fill="none"
                        stroke="#ffffff"
                        strokeWidth={2.5}
                        strokeLinejoin="round"
                        textAnchor={label.align ?? 'middle'}
                      >
                        {label.text}
                      </text>
                      <text
                        x={label.x}
                        y={label.y}
                        fontSize={3.8}
                        fill="#374151"
                        textAnchor={label.align ?? 'middle'}
                        fontWeight={600}
                      >
                        {label.text}
                      </text>
                      {/* Debug mode: show position coordinates */}
                      {config.showDebugLabels && (
                        <>
                          <circle cx={label.x} cy={label.y} r={1} fill="#ef4444" opacity={0.7} />
                          <text
                            x={label.x}
                            y={label.y + 4}
                            fontSize={2.5}
                            fill="#ef4444"
                            textAnchor="middle"
                            fontWeight="bold"
                          >
                            ({label.x?.toFixed(1)}, {label.y?.toFixed(1)})
                          </text>
                        </>
                      )}
                    </g>
                  ))}

                  {/* Debug mode: show all point coordinates */}
                  {config.showDebugLabels && points.map((point, index) => (
                    <text
                      key={`debug-point-${index}`}
                      x={point.x}
                      y={point.y + 6}
                      fontSize={2.2}
                      fill="#3b82f6"
                      textAnchor="middle"
                      fontWeight="bold"
                    >
                      {point.label}: ({point.x}, {point.y})
                    </text>
                  ))}
                </g>
              </svg>
              {/* Debug panel showing all label positions */}
              {config.showDebugLabels && (
                <Box sx={{ mt: 1, p: 1, bgcolor: '#fef3c7', borderRadius: 1, fontSize: '10px', fontFamily: 'monospace' }}>
                  <Typography variant="caption" fontWeight="bold" display="block">Debug: Label Positions</Typography>
                  {resolvedSideLabels.map((label, idx) => (
                    <Typography key={idx} variant="caption" display="block" sx={{ fontSize: '9px' }}>
                      "{label.text}": x={label.x?.toFixed(1)}, y={label.y?.toFixed(1)}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          );
        }

        case 'composed': {
          // Composed chart - bar chart with multiple data series or mixed types
          const composedBaseKeys = chartData[0] ? Object.keys(chartData[0]) : [];
          
          // Auto-detect x-axis key
          let composedXKey = 'name';
          if (composedBaseKeys.includes('name')) composedXKey = 'name';
          else if (composedBaseKeys.includes('category')) composedXKey = 'category';
          else if (composedBaseKeys.includes('label')) composedXKey = 'label';
          else if (composedBaseKeys.length > 0) composedXKey = composedBaseKeys[0];
          
          // Get numeric keys for bars
          const numericKeys = composedBaseKeys.filter(k => 
            k !== composedXKey && typeof chartData[0][k] === 'number'
          );
          const keys = dataKeys.length > 0 ? dataKeys : numericKeys;
          
          return (
            <BarChart data={chartData} margin={commonMargin}>
              {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />}
              <XAxis
                dataKey={composedXKey}
                label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -5 } : undefined}
                stroke="#666"
              />
              <YAxis
                label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft' } : undefined}
                domain={yDomain}
                stroke="#666"
              />
              <Tooltip content={<CustomTooltip />} />
              {showLegend && <Legend />}
              {keys.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  fill={COLORS[index % COLORS.length]}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          );
        }

        case 'circle': {
          // Circle visualization for geometry problems
          const circleConfig = config.circleConfig || {
            radius: 50,
            showRadius: true,
            showDiameter: false,
            showCenter: true,
          };
          
          const {
            radius = 50,
            showRadius = true,
            showDiameter = false,
            showCenter = true,
            radiusLabel = '',
            diameterLabel = '',
            centerLabel = 'O',
            strokeColor = '#4f46e5',
            fillColor = '#e0e7ff',
            height: circleHeight = 300,
          } = circleConfig;

          const viewBoxSize = 100;
          const centerX = viewBoxSize / 2;
          const centerY = viewBoxSize / 2;
          const scaledRadius = 35; // Fixed visual radius in viewBox units

          return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <svg
                viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
                preserveAspectRatio="xMidYMid meet"
                style={{ width: '100%', maxWidth: '350px', height: `${circleHeight}px`, display: 'block' }}
              >
                {/* Circle */}
                <circle
                  cx={centerX}
                  cy={centerY}
                  r={scaledRadius}
                  fill={fillColor}
                  stroke={strokeColor}
                  strokeWidth={2.5}
                  opacity={0.8}
                />
                
                {/* Center point */}
                {showCenter && (
                  <>
                    <circle cx={centerX} cy={centerY} r={2} fill={strokeColor} />
                    <text
                      x={centerX - 5}
                      y={centerY - 5}
                      fontSize="6"
                      fill={strokeColor}
                      fontWeight="bold"
                    >
                      {centerLabel}
                    </text>
                  </>
                )}

                {/* Radius line */}
                {showRadius && (
                  <>
                    <line
                      x1={centerX}
                      y1={centerY}
                      x2={centerX + scaledRadius}
                      y2={centerY}
                      stroke="#dc2626"
                      strokeWidth={2}
                    />
                    <circle cx={centerX + scaledRadius} cy={centerY} r={2} fill="#dc2626" />
                    <text
                      x={centerX + scaledRadius / 2}
                      y={centerY - 5}
                      fontSize="5"
                      fill="#dc2626"
                      textAnchor="middle"
                      fontWeight="bold"
                    >
                      {radiusLabel || `r = ${radius}`}
                    </text>
                  </>
                )}

                {/* Diameter line */}
                {showDiameter && (
                  <>
                    <line
                      x1={centerX - scaledRadius}
                      y1={centerY}
                      x2={centerX + scaledRadius}
                      y2={centerY}
                      stroke="#059669"
                      strokeWidth={2}
                      strokeDasharray="3 2"
                    />
                    <circle cx={centerX - scaledRadius} cy={centerY} r={2} fill="#059669" />
                    <circle cx={centerX + scaledRadius} cy={centerY} r={2} fill="#059669" />
                    <text
                      x={centerX}
                      y={centerY + scaledRadius + 10}
                      fontSize="5"
                      fill="#059669"
                      textAnchor="middle"
                      fontWeight="bold"
                    >
                      {diameterLabel || `d = ${radius * 2}`}
                    </text>
                  </>
                )}
              </svg>
            </Box>
          );
        }

        case '3d-shape': {
          const shape3DConfig = config.shape3DConfig;
          if (!shape3DConfig) {
            return (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No 3D shape configuration provided.</Typography>
              </Box>
            );
          }
          const dims = shape3DConfig.dimensions ?? {};
          const unit = shape3DConfig.unit ?? 'cm';
          const color = shape3DConfig.color;

          switch (shape3DConfig.shapeType) {
            case 'rectangular_prism':
              return <RectangularPrism length={dims.length} width={dims.width} height={dims.height} unit={unit} color={color} />;
            case 'cube':
              return <Cube side={dims.side} unit={unit} color={color} />;
            case 'sphere':
              return <Sphere radius={dims.radius} unit={unit} color={color} />;
            case 'cylinder':
              return <Cylinder radius={dims.radius} height={dims.height} unit={unit} color={color} />;
            case 'cone':
              return <Cone radius={dims.radius} height={dims.height} slantHeight={dims.slantHeight} unit={unit} color={color} />;
            case 'pyramid':
              return <Pyramid base={dims.base} height={dims.height} slantHeight={dims.slantHeight} unit={unit} color={color} />;
            case 'triangular_prism':
              return <TriangularPrism base={dims.base} height={dims.height} length={dims.length} unit={unit} color={color} />;
            case 'hemisphere':
              return <Hemisphere radius={dims.radius} unit={unit} color={color} />;
            case 'silo':
              return <Silo2D radius={dims.radius} cylinderHeight={dims.cylinderHeight} unit={unit} color={color} />;
            default:
              return (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">Unsupported 3D shape: {String(shape3DConfig.shapeType)}</Typography>
                </Box>
              );
          }
        }

        default:
          return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">Unsupported graph type: {type}</Typography>
            </Box>
          );
      }
    } catch (error) {
      console.error('Error rendering graph:', error);
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error">Error rendering graph. Please check the data format.</Typography>
        </Box>
      );
    }
  };

  const isCustomVisual = type === 'fraction-rectangle' || type === 'fraction-circle' || type === 'polygon' || type === 'circle' || type === '3d-shape' || type === 'table' || type === 'dotplot' || type === 'number_line' || type === 'coordinate_plane';

  return (
    <Box 
      className={className}
      sx={{ 
        bgcolor: 'white', 
        border: '1px solid #e0e0e0', 
        borderRadius: 2, 
        p: 2, 
        my: 2 
      }}
    >
      {/* Hide title for 3D shapes in worksheets, show for other chart types */}
      {title && type !== '3d-shape' && (
        <Typography 
          variant="subtitle1" 
          textAlign="center" 
          fontWeight={600} 
          color="text.primary" 
          mb={2}
        >
          {title}
        </Typography>
      )}
      {isCustomVisual ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>{renderChart()}</Box>
      ) : (
        <ResponsiveContainer width="100%" height={350}>
          {renderChart()}
        </ResponsiveContainer>
      )}
    </Box>
  );
};

export default GraphRenderer;
